import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {XIcon} from '@primer/octicons-react'
import {Box, Flash, Header, Link, Octicon} from '@primer/react'
import {memo, useCallback, useLayoutEffect, useRef, useState} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import {type SidePanelItem, SidePanelTypeParam} from '../../api/memex-items/side-panel-item'
import type {SuggestedRepository} from '../../api/repository/contracts'
import {
  ProjectDescriptionAdd,
  ProjectDescriptionSidePanelUI,
  ProjectDescriptionUpdate,
  ProjectReadmeAdd,
  ProjectReadmeSettingsPageUI,
  ProjectReadmeUpdate,
} from '../../api/stats/contracts'
import {assertNever} from '../../helpers/assert-never'
import {resetScrollPositionImmediately} from '../../helpers/scroll-utilities'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import type {SidePanelState} from '../../hooks/use-side-panel'
import {useSidePanelDirtyState} from '../../hooks/use-side-panel-dirty-state'
import {IssueStateProvider} from '../../state-providers/issues/issue-state-provider'
import {useIssueContext} from '../../state-providers/issues/use-issue-context'
import {useProjectDetails} from '../../state-providers/memex/use-project-details'
import {DescriptionEditor} from '../description-editor'
import {SanitizedHtml} from '../dom/sanitized-html'
import type {OmnibarItemAttrs} from '../omnibar/types'
import {ShortDescriptionEditor} from '../short-description-editor'
import {StatusUpdates} from '../status-updates/status-updates'
import {SidePanelBody} from './body'
import {BulkAddItemsProvider} from './bulk-add/bulk-add-items-provider'
import {BulkAddView} from './bulk-add/bulk-add-view'
import {SidePanelComments} from './comments'
import {SidePanelHeader, type SidePanelTabName, SidePanelToolbar} from './header'
import {SharedIssueViewer} from './shared-issue-viewer'
import {SidePanelSidebar} from './sidebar'

export const SidePanelContent = memo(function SidePanelContent({
  sidePanelState,
}: {
  sidePanelState: NonNullable<SidePanelState>
}) {
  const {memex_new_issue_viewer} = useEnabledFeatures()

  switch (sidePanelState.type) {
    case SidePanelTypeParam.ISSUE: {
      if (memex_new_issue_viewer && sidePanelState.item.contentType === 'Issue') {
        const issueIdentifier = sidePanelState.item.getItemIdentifier()
        return issueIdentifier ? (
          <SharedIssueViewer {...issueIdentifier} url={sidePanelState.item.getUrl()} itemId={sidePanelState.item.id} />
        ) : null
      }

      return (
        <IssueStateProvider
          contentType={sidePanelState.item.contentType}
          repositoryId={sidePanelState.item.ownerId()}
          itemId={sidePanelState.item.itemId()}
          memexItemId={sidePanelState.item.memexItemId?.()}
        >
          <SidePanelItemContent item={sidePanelState.item} />
        </IssueStateProvider>
      )
    }
    case SidePanelTypeParam.INFO: {
      return <SidePanelMemexInfoContent />
    }
    case SidePanelTypeParam.BULK_ADD: {
      return (
        <SidePanelMemexBulkAddContent
          targetRepository={sidePanelState.targetRepository}
          query={sidePanelState.query}
          newItemAttributes={sidePanelState.newItemAttributes}
        />
      )
    }
    default: {
      assertNever(sidePanelState)
    }
  }
})

const SidePanelMemexInfoContent = memo(function SidePanelMemexInfoContent() {
  const {postStats} = usePostStats()
  const {titleHtml} = useProjectDetails()
  const {description} = useProjectDetails()
  const {hasWritePermissions} = ViewerPrivileges()
  const [, setHasUnsavedShortDescription] = useSidePanelDirtyState()
  const [, setHasUnsavedDescription] = useSidePanelDirtyState()
  const {memex_status_updates} = useEnabledFeatures()

  const onSaveStatsShortDescription = useCallback(
    (update: boolean) => {
      postStats({
        name: update ? ProjectDescriptionUpdate : ProjectDescriptionAdd,
        ui: ProjectDescriptionSidePanelUI,
      })
    },
    [postStats],
  )

  const onSaveStatsDescription = useCallback(
    (update: boolean) => {
      postStats({
        name: update ? ProjectReadmeUpdate : ProjectReadmeAdd,
        ui: ProjectReadmeSettingsPageUI,
      })
    },
    [postStats],
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'canvas.default',
        width: '100%',
        overflowY: 'auto',
        height: '100%',
        alignItems: 'center',
        px: '20px',
      }}
    >
      <Box sx={{width: '100%', mt: 4}}>
        <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'row-reverse'}}>
          <SidePanelToolbar showCloseButton />
          <Box sx={{flex: 1}} />
          <SanitizedHtml sx={{fontWeight: '600', fontSize: 2, wordBreak: 'break-word'}} as="h2">
            {titleHtml}
          </SanitizedHtml>
        </Box>

        <ShortDescriptionEditor
          onSaveStats={onSaveStatsShortDescription}
          setHasUnsavedChanges={setHasUnsavedShortDescription}
          hideLabel
        />
      </Box>
      {(!hasWritePermissions && !!description) || hasWritePermissions ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 'auto',
            flexGrow: 0,
            width: '100%',
          }}
        >
          <DescriptionEditor
            onSaveStats={onSaveStatsDescription}
            setHasUnsavedChanges={setHasUnsavedDescription}
            hideLabel
          />
        </Box>
      ) : null}
      {memex_status_updates && <StatusUpdates />}
    </Box>
  )
})

// Could just get supportsComments and isLoading from the useIssueContext hook, but this helps minimize hook use
// to minimize re-renders
const SidePanelItemDetails = memo<{item: SidePanelItem; isLoading: boolean}>(function SidePanelItemDetails({
  item,
  isLoading,
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const itemId = item.itemId()

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      resetScrollPositionImmediately(scrollContainerRef.current)
    }
  }, [itemId])

  const breakpoint = useContainerBreakpoint(scrollContainerRef.current)

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        display: 'flex',
        flex: 'auto',
        flexDirection: breakpoint(['column', 'column', 'row', 'row']),
        backgroundColor: 'canvas.default',
        justifyContent: 'stretch',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 'auto',
          flexDirection: 'column',
          width: breakpoint(['100%', '100%', '66%', '66%']),
          mx: 'auto',
        }}
      >
        <SidePanelBody item={item} isLoading={isLoading} />
        <SidePanelComments itemURL={item.getUrl()} />
      </Box>
      <SidePanelSidebar item={item} breakpoint={breakpoint} />
    </Box>
  )
})

const SidePanelItemContent = memo<{
  item: SidePanelItem
}>(function SidePanelItemContent({item}) {
  const [showBanner, setShowBanner] = useState(true)
  const [selectedTab, setSelectedTab] = useState<SidePanelTabName>('details')
  const {tasklist_block, memex_new_issue_viewer} = useEnabledFeatures()
  const {isLoading} = useIssueContext()
  const showBreadcrumbs = tasklist_block && item.contentType === ItemType.Issue
  const showTabs = false // Tabs are permanently disabled pending future hierarchy works
  const shouldShowBanner =
    !memex_new_issue_viewer && (process.env.APP_ENV === 'development' || process.env.APP_ENV === 'staging')

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: 'canvas.default',
        height: '100%',
        width: '100%',
        flexGrow: 1,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {shouldShowBanner && showBanner && (
        <Header.Item full>
          <Flash variant="warning" onClick={() => setShowBanner(false)} as="button">
            The memex issue viewer will be deprecated soon. For new features and bug fixes, we ask you to work in the
            new issue viewer (in ui/packages/issue-viewer).{' '}
            <Link href="https://github.com/github/issues/discussions/6872" target="_blank">
              Learn more
            </Link>{' '}
            <Octicon icon={XIcon} />
          </Flash>
        </Header.Item>
      )}
      <SidePanelHeader
        item={item}
        isLoading={isLoading}
        showBreadcrumbs={showBreadcrumbs}
        showTabs={showTabs}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
      />
      <SidePanelItemDetails item={item} isLoading={isLoading} />
    </Box>
  )
})

const SidePanelMemexBulkAddContent = memo<{
  targetRepository?: SuggestedRepository
  query?: string
  newItemAttributes?: OmnibarItemAttrs
}>(function SidePanelMemexBulkAddContent({targetRepository, query, newItemAttributes}) {
  return (
    <BulkAddItemsProvider query={query} selectedRepository={targetRepository}>
      <BulkAddView targetRepository={targetRepository} newItemAttributes={newItemAttributes} />
    </BulkAddItemsProvider>
  )
})

try{ SidePanelContent.displayName ||= 'SidePanelContent' } catch {}
try{ SidePanelMemexInfoContent.displayName ||= 'SidePanelMemexInfoContent' } catch {}
try{ SidePanelItemDetails.displayName ||= 'SidePanelItemDetails' } catch {}
try{ SidePanelItemContent.displayName ||= 'SidePanelItemContent' } catch {}
try{ SidePanelMemexBulkAddContent.displayName ||= 'SidePanelMemexBulkAddContent' } catch {}